import { KameleoonVisitorCodeManager } from '@kameleoon/nextjs-visitor-code-manager';
import { KameleoonEventSource } from '@kameleoon/nodejs-event-source';
import type { GetServerSidePropsContext } from 'next';
import {
  type CredentialsType,
  type SDKConfigurationType,
  BrowserType,
  CustomData,
  DeviceType,
  KameleoonClient,
  KameleoonError,
  UserAgent,
  Browser,
  Device,
  UniqueIdentifier,
} from '@kameleoon/nodejs-sdk';
import { KameleoonRequester } from '@kameleoon/nodejs-requester';
import { isPharmaprixBanner } from '@/utils';
import { UAParser } from 'ua-parser-js';
//@ts-expect-error -- types mismatch
import { isBot } from 'ua-parser-js/bot-detection';
//@ts-expect-error -- types mismatch
import { DeviceType as DeviceTypeUA } from 'ua-parser-js/enums';
import { KAMELEOON, KamErrors, KameleoonDomains, KameleoonEnvironment } from './constants';
import { TKamFlags } from '@/store/kameleoonFlags/kameleoonFlags.interface';
import { getSnowplowUserData } from '@/utils/analytics/analytics-utils';

// Request deduplication cache
const requestCache = new Map<string, Promise<any>>();

// Custom requester with better error handling and deduplication
class CustomKameleoonRequester extends KameleoonRequester {
  async request(url: string, options: RequestInit): Promise<Response> {
    const cacheKey = `${url}-${JSON.stringify(options)}`;

    // Check if request is already in progress
    if (requestCache.has(cacheKey)) {
      console.log(`Kameleoon: Deduplicating request to ${url}`);
      return requestCache.get(cacheKey);
    }

    // Create new request promise
    const requestPromise = this.executeRequest(url, options);
    requestCache.set(cacheKey, requestPromise);

    try {
      const result = await requestPromise;
      return result;
    } finally {
      // Clean up cache after request completes
      requestCache.delete(cacheKey);
    }
  }

  private async executeRequest(url: string, options: RequestInit): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof Error && error.name === 'AbortError') {
        console.warn(`Kameleoon: Request to ${url} was aborted due to timeout`);
        throw new Error('Request timeout');
      }

      throw error;
    }
  }
}

function getKamBrowserType(parser: UAParser) {
  const browserName = parser.getBrowser().name?.toUpperCase();
  if (!browserName) return BrowserType.Other;
  if (browserName.includes(BrowserType.Chrome)) return BrowserType.Chrome;
  if (browserName.includes(BrowserType.Firefox)) return BrowserType.Firefox;
  if (browserName.includes(BrowserType.Safari)) return BrowserType.Safari;
  if (browserName.includes(BrowserType.Opera)) return BrowserType.Opera;
  return BrowserType.Other;
}

function getKamDeviceType(parser: UAParser) {
  const deviceType = parser.getDevice().type;
  if (deviceType === DeviceTypeUA.MOBILE) return DeviceType.Phone;
  if (deviceType === DeviceTypeUA.TABLET) return DeviceType.Tablet;
  return DeviceType.Desktop;
}

let client: KameleoonClient | null;
let initializationPromise: Promise<KameleoonClient | null> | null = null;

/**
 * A custom errorHandler to print error messages
 * @param {string} type - Provide an identifier to know which function is receiving an error
 * @param {unknown} error - Caught error from try/catch, this will be a JavaScript Error or the custom KameleoonError from SDK
 * @returns undefined
 */
const errorHandler = (type: string, error: unknown): void => {
  if (error instanceof KameleoonError) {
    console.log(`${error.type}: ${error.message}`);
    return;
  } else {
    console.log(`KamSdk ${type}: ${error}`);
  }
};

export const setupKameleoonSSR = async (
  req: GetServerSidePropsContext['req'],
  res: GetServerSidePropsContext['res'],
  id?: string,
  secret?: string
) => {
  const kamClient = await getKameleoonClient(id, secret);
  const { domainUserId } = getSnowplowUserData(req);
  const userAgentString = req.headers["user-agent"] || KAMELEOON.USER_AGENT_BOT;

  const visitorCode = kamClient?.getVisitorCode({
    request: req,
    response: res,
  });

  if (domainUserId && visitorCode && kamClient) {
    const userIdentifierData = new CustomData(isPharmaprixBanner(req) ? 7 : 10, domainUserId);
    kamClient.addData(visitorCode, userIdentifierData);
    // Flushing data for the anonymous `visitorCode`
    kamClient.flush(visitorCode);
    // Link the visitor code to the snowplow user id
    kamClient.addData(domainUserId, new UniqueIdentifier(true));
  }

  const updatedVisitorCode = domainUserId || visitorCode || '';

  if (updatedVisitorCode && kamClient) {
    const parser = new UAParser(userAgentString);

    kamClient.addData(
      updatedVisitorCode,
      new UserAgent(isBot(userAgentString) ? KAMELEOON.USER_AGENT_BOT : userAgentString)
    );
    kamClient.addData(updatedVisitorCode, new CustomData(isPharmaprixBanner(req) ? 8 : 11, userAgentString));
    kamClient.addData(updatedVisitorCode, new Browser(getKamBrowserType(parser)));
    kamClient.addData(updatedVisitorCode, new Device(getKamDeviceType(parser)));
  }

  return updatedVisitorCode;
};

export const getKameleoonClient = async (id?: string, secret?: string): Promise<KameleoonClient | null> => {
  // If client is already initialized, return it
  if (client && client.isInitialized()) {
    return client;
  }

  // If initialization is already in progress, wait for it
  if (initializationPromise) {
    console.log('Kameleoon: Waiting for existing initialization to complete');
    return initializationPromise;
  }

  // Start new initialization and cache the promise
  console.log('Kameleoon: Starting new client initialization');
  initializationPromise = initializeKameleeonSDK(id, secret);
  const result = await initializationPromise;

  // Clear the promise cache after completion
  initializationPromise = null;

  return result;
};

// initializing the global client for application (we are not connecting client data at this point)
// This should only be called server-side to avoid exposing CLIENT_SECRET
const initializeKameleeonSDK = async (id?: string, secret?: string): Promise<KameleoonClient | null> => {
  // Prevent initialization on client-side where CLIENT_SECRET would be exposed
  if (typeof window !== 'undefined') {
    console.warn('Kameleoon: SDK initialization skipped on client-side. Use API route instead.');
    return null;
  }

  // const clientId = process.env.KAMELEOON_CLIENT_ID;
  // const clientSecret = process.env.KAMELEOON_CLIENT_SECRET;

  if (!id || !secret) {
    console.error('Kameleoon: Missing required environment variables');
    return null;
  }

  // -- Mandatory credentials (server-side only)
  const credentials: CredentialsType = {
    clientId: id,
    clientSecret: secret,
  };

  //Get Kam Domain
  const getDomain = (env: KameleoonEnvironment): KameleoonDomains => {
    switch (env) {
      case KameleoonEnvironment.Development:
        return KameleoonDomains.Development;
      case KameleoonEnvironment.Staging:
        return KameleoonDomains.Staging;
      case KameleoonEnvironment.Production:
        return KameleoonDomains.Production;
      case KameleoonEnvironment.Lowers:
        return KameleoonDomains.Lowers;
      default:
        return KameleoonDomains.Development;
    }
  };

  const env = process.env.NEXT_PUBLIC_KAMELEOON_ENV as KameleoonEnvironment;

  // -- Optional configuration
  const configuration: Partial<SDKConfigurationType> = {
    updateInterval: 5,
    environment: env,
    cookieDomain: getDomain(env),
    targetingDataCleanupInterval: 5,
  };

  try {
    client = new KameleoonClient({
      siteCode: KAMELEOON.SITE_CODE,
      credentials,
      configuration,
      externals: {
        visitorCodeManager: new KameleoonVisitorCodeManager(),
        eventSource: new KameleoonEventSource(),
        requester: new CustomKameleoonRequester(),
      },
    });
    await client.initialize();
  } catch (error) {
    errorHandler(KamErrors.Init, error);
    client = null;
  }

  return client;
};

// Clear request cache (useful for memory management)
export const clearKameleoonRequestCache = () => {
  requestCache.clear();
  console.log('Kameleoon: Request cache cleared');
};

// Client-side function to fetch Kameleoon flags when visitor code becomes available
// This calls the API route instead of directly using the SDK to avoid exposing CLIENT_SECRET
export const fetchKameleoonFlagsClientSide = async (visitorCode: string, warehouseKey: string): Promise<TKamFlags> => {
  if (!visitorCode) {
    console.log('Kameleoon: No visitor code available on client side yet');
    return {};
  }

  try {
    // Call the API route instead of directly using the SDK
    // This ensures CLIENT_SECRET is never exposed to the client
    const params = new URLSearchParams({
      visitorCode,
      ...(warehouseKey && warehouseKey !== visitorCode ? { warehouseKey } : {}),
    });

    const response = await fetch(`/api/kameleoon/flags?${params.toString()}`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to fetch flags: ${response.statusText}`);
    }

    const data = await response.json();
    const flags = data.flags || {};

    return flags;
  } catch (error) {
    console.error('Kameleoon: Error fetching flags on client side:', error);
    return {};
  }
};

// this is where we are using client-specific data
export const setWarehouseData = async (visitorCode: string, warehouseKey: string) => {
  try {
    const warehouseData = await client?.getVisitorWarehouseAudience({
      visitorCode: visitorCode, // spid (snowplow id)
      warehouseKey: warehouseKey, // pcid or spid if user is guest/anonymous
      customDataIndex: 5, //pcid /spid warehousekey index value of 5 corresponds to Kam Dashboard setup
    });

    if (!warehouseData) {
      client?.addData(visitorCode, new CustomData(5, ''));
    } else {
      client?.addData(visitorCode, warehouseData);
    }
  } catch (error) {
    errorHandler(KamErrors.WarehouseKey, error);
  }
};

export const getKameleoonFlags = async (visitorCode: string, warehouseKey: string): Promise<TKamFlags> => {
  if (!visitorCode) {
    console.warn('Kameleoon: No visitor code provided, skipping feature flags');
    return {};
  }

  // Ensure client is initialized
  const kamClient = await getKameleoonClient();
  if (!kamClient || !kamClient.isInitialized()) {
    console.warn('Kameleoon: Client not available or not initialized');
    return {};
  }

  try {
    await setWarehouseData(visitorCode, warehouseKey);
    const activeFlags = kamClient.getVariations({ visitorCode, onlyActive: true });
    return Array.from(activeFlags.entries()).reduce((featureFlags, lineItem) => {
      const [key, value] = lineItem;
      return {
        ...featureFlags,
        [key]: {
          ...value,
          variation: value?.key ?? '',
          variables: Array.from(value?.variables?.values() || []),
        },
      };
    }, {});
  } catch (error) {
    // Handle specific "body used already" errors
    if (error instanceof Error && error.message.includes('body used already')) {
      console.warn('Kameleoon: Body used already error detected, clearing request cache and retrying');
      clearKameleoonRequestCache();
      // Return empty flags to prevent further issues
      return {};
    }

    errorHandler(KamErrors.WarehouseKey, error);
    return {};
  }
};
